import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';

export type BannerTone = 'warning' | 'info';

export type LecturerTone = 'agentFault';

export type FaultPresentation = {
  studentBanner: { copyKey: string; defaultValue: string; tone: BannerTone } | null;
  lecturerLine: { copyKey: string; defaultValue: string; tone: LecturerTone };
  recoveryCta?: 'resume' | 'leave';
};

const AGENT_RECONNECT: FaultPresentation = {
  studentBanner: {
    copyKey: 'oralExam.student.examinerReconnecting.title',
    defaultValue: 'Reconnecting',
    tone: 'info',
  },
  lecturerLine: {
    copyKey: 'oralExam.lecturer.cohort.stateAgentReconnecting',
    defaultValue: 'Reconnecting — timer paused',
    tone: 'agentFault',
  },
};

export const FAULT_PRESENTATION: Record<OralExamOperationalFault, FaultPresentation> = {
  [OralExamOperationalFault.failover_required]: AGENT_RECONNECT,
  [OralExamOperationalFault.agent_failed]: AGENT_RECONNECT,
  [OralExamOperationalFault.student_disconnected]: {
    studentBanner: {
      copyKey: 'oralExam.student.fault.reconnectedBanner',
      defaultValue: "You're back online — your answers are saved. Continue when you're ready.",
      tone: 'info',
    },
    lecturerLine: {
      copyKey: 'oralExam.lecturer.cohort.stateStudentDisconnected',
      defaultValue: 'Disconnected — reconnecting',
      tone: 'agentFault',
    },
  },
  [OralExamOperationalFault.recording_failed]: {
    studentBanner: {
      copyKey: 'oralExam.student.fault.recordingInterruptedBanner',
      defaultValue: 'Recording was interrupted. The exam can continue.',
      tone: 'warning',
    },
    lecturerLine: {
      copyKey: 'oralExam.lecturer.cohort.stateRecordingFailed',
      defaultValue: 'Recording interrupted — decide whether to continue, abort, or reschedule',
      tone: 'agentFault',
    },
  },
  [OralExamOperationalFault.session_expired]: {
    studentBanner: null,
    lecturerLine: {
      copyKey: 'oralExam.lecturer.cohort.stateSessionExpired',
      defaultValue: 'Session expired',
      tone: 'agentFault',
    },
    recoveryCta: 'leave',
  },
};

/** Resolve fault copy. A missing slot size still says Reconnecting. */
export function resolveFaultPresentation(
  fault: OralExamOperationalFault,
  // Capacity used to choose takeover copy. Every size now shares one presentation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  options: { capacity?: number | null } = {},
): FaultPresentation {
  return FAULT_PRESENTATION[fault];
}

/** Compile-time exhaustiveness helper for switch statements over OralExamOperationalFault. */
const PRACTICE_AGENT_BANNER = {
  copyKey: 'oralExam.student.fault.practiceAgentBanner',
  defaultValue: "The AI examiner didn't join. Leave and try the dry run again.",
  tone: 'warning' as BannerTone,
};

/** Graded failover copy must never appear on a self-service dry-run session. */
export function studentFaultBanner(
  fault: OralExamOperationalFault,
  { selfService, capacity }: { selfService: boolean; capacity?: number | null },
): FaultPresentation['studentBanner'] {
  if (
    selfService
    && (
      fault === OralExamOperationalFault.failover_required
      || fault === OralExamOperationalFault.agent_failed
    )
  ) {
    return PRACTICE_AGENT_BANNER;
  }
  return resolveFaultPresentation(fault, { capacity }).studentBanner;
}

export function assertNever(value: never): never {
  throw new Error(`Unexpected operational fault: ${String(value)}`);
}

/** 1:N terminal copy when the exam ends as agent_unavailable (stored endReason). */
export const AGENT_UNAVAILABLE_TERMINAL = {
  title: {
    copyKey: 'oralExam.student.examEndedConnection.title',
    defaultValue: 'Sorry, there is a difficulty on our end',
  },
  body: {
    copyKey: 'oralExam.student.examEndedConnection.body',
    defaultValue: 'Please reschedule. This does not count as an attempt.',
  },
} as const;

/** Self-service dry-run terminal when the examiner ends as agent_unavailable. */
export const DRY_RUN_AGENT_UNAVAILABLE_TERMINAL = {
  title: {
    copyKey: 'oralExam.practice.agentUnavailable.title',
    defaultValue: 'Examiner problem',
  },
  body: {
    copyKey: 'oralExam.practice.agentUnavailable.body',
    defaultValue: 'The examiner had a problem. Start the dry-run again.',
  },
  retry: {
    copyKey: 'oralExam.practice.startCta',
    defaultValue: 'Start dry run',
  },
} as const;
